import React, { useState, useEffect, useMemo } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    PictureAsPdf as PdfIcon,
    TableChart as ExcelIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import AssignmentService from '../../../Services/assignment.service';
import UserService from '../../../Services/user.service';
import Constants from '../../../assests/Constants/constants';
import UserAvatar from '../../Widgets/UserAvatar/UserAvatar.js';
import XtramModal, {
    XtramBadge,
    XtramButton,
    XtramField,
    XtramSelect,
    XtramStat,
    XtramTable,
    xtramStyles as styles,
} from '../../Widgets/XtramModal/XtramModal';
import { priorityEmoji, priorityKey, priorityTone, statusKey, statusTone } from './assignmentDisplay';

const ALL = 'all';
const PREVIEW_ROWS = 5;

const AssignmentReportManager = ({ open, onClose, assignments, subAccountants, caseIdOptions, categoryOptions, startDate, endDate }) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const [loading, setLoading] = useState(false);
    const [reportFilters, setReportFilters] = useState({
        caseId: null,
        assigneeId: null,
        status: null
    });

    const [user, setUser] = useState(null);
    const [token, setToken] = useState('');

    useEffect(() => {
        const fetchUserData = async () => {
            const currentUser = await UserService.getCurrentUser();
            const currentToken = await UserService.getCurrentToken();
            setUser(currentUser);
            setToken(currentToken);
        };
        fetchUserData();
    }, []);

    const clientOptions = useMemo(() => [
        { value: ALL, label: t('AllClients') },
        ...caseIdOptions.filter((option) => option.value),
    ], [caseIdOptions, t]);

    const assigneeOptions = useMemo(() => [
        { value: ALL, label: t('AllAssignees') },
        ...subAccountants.map((assignee) => ({ value: assignee.subAccountantId, label: assignee.name })),
    ], [subAccountants, t]);

    const statusOptions = useMemo(() => [
        { value: ALL, label: t('AllStatuses') },
        { value: Constants.StatusEnum.ToDo, label: t('ToDo') },
        { value: Constants.StatusEnum.InProgress, label: t('InProgress') },
        { value: Constants.StatusEnum.Done, label: t('Done') },
    ], [t]);

    const pickFilter = (field) => (option) => setReportFilters((prev) => ({
        ...prev,
        [field]: !option || option.value === ALL ? null : option.value,
    }));

    const selectedOption = (options, value) => options.find((o) => o.value === (value ?? ALL)) || options[0];

    const filteredAssignments = useMemo(() => {
        let filtered = assignments;

        if (reportFilters.caseId) {
            filtered = filtered.filter(a => a.caseId === reportFilters.caseId);
        }

        if (reportFilters.assigneeId) {
            filtered = filtered.filter(a => a.assigneeId === reportFilters.assigneeId);
        }

        if (reportFilters.status !== null) {
            filtered = filtered.filter(a => a.status === reportFilters.status);
        }

        return filtered;
    }, [assignments, reportFilters]);

    const todoAssignments = useMemo(() =>
        filteredAssignments.filter(a => a.status === Constants.StatusEnum.ToDo),
        [filteredAssignments]
    );

    const inProgressAssignments = useMemo(() =>
        filteredAssignments.filter(a => a.status === Constants.StatusEnum.InProgress),
        [filteredAssignments]
    );

    const doneAssignments = useMemo(() =>
        filteredAssignments.filter(a => a.status === Constants.StatusEnum.Done),
        [filteredAssignments]
    );

    const buildRequest = () => ({
        accountantId: user.accountantId,
        startDate: startDate || dayjs().startOf('month').toDate(),
        endDate: endDate || dayjs().endOf('month').toDate(),
        caseId: reportFilters.caseId || null,
        assigneeId: reportFilters.assigneeId || null,
        status: reportFilters.status
    });

    const handleDownloadPdf = async () => {
        if (!user?.accountantId) {
            toast.error(t('UserNotAuthenticated'));
            return;
        }

        setLoading(true);
        try {
            await AssignmentService.GenerateAssignmentReportPdf(buildRequest(), token);
            toast.success(t('PdfDownloadedSuccessfully'));
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error(t('ErrorDownloadingPdf'));
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadExcel = async () => {
        if (!user?.accountantId) {
            toast.error(t('UserNotAuthenticated'));
            return;
        }

        setLoading(true);
        try {
            await AssignmentService.GenerateAssignmentReportExcel(buildRequest(), token);
            toast.success(t('ExcelDownloadedSuccessfully'));
        } catch (error) {
            console.error('Error downloading Excel:', error);
            toast.error(t('ErrorDownloadingExcel'));
        } finally {
            setLoading(false);
        }
    };

    const getAssigneeName = (assigneeId) => {
        const assignee = subAccountants.find(sa => sa.subAccountantId === assigneeId);
        return assignee ? assignee.name : t('NoAssignee');
    };

    const getClientName = (caseId) => {
        const client = caseIdOptions.find(c => c.value === caseId);
        return client ? client.label : t('Office');
    };

    const renderAssignmentTable = (rows, title, tone) => (
        <div className={styles.section}>
            <div className={styles.toolbar}>
                <h3 className={styles.sectionTitle}>{title}</h3>
                <XtramBadge tone={tone}>{rows.length}</XtramBadge>
            </div>
            <XtramTable height="medium">
                <Table size="small" stickyHeader dir={direction}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('Summary')}</TableCell>
                            <TableCell>{t('Assignee')}</TableCell>
                            <TableCell>{t('Client')}</TableCell>
                            <TableCell>{t('Priority')}</TableCell>
                            <TableCell>{t('Status')}</TableCell>
                            <TableCell>{t('DueDate')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className={styles.emptyState}>
                                    {t('NoAssignmentsFound')}
                                </TableCell>
                            </TableRow>
                        ) : rows.slice(0, PREVIEW_ROWS).map((assignment) => (
                            <TableRow key={assignment.id} hover>
                                <TableCell className={styles.wrapCell}>{assignment.summary}</TableCell>
                                <TableCell>
                                    <UserAvatar name={getAssigneeName(assignment.assigneeId)} size={18} />
                                </TableCell>
                                <TableCell>
                                    <UserAvatar name={getClientName(assignment.caseId)} size={18} />
                                </TableCell>
                                <TableCell>
                                    <XtramBadge tone={priorityTone(assignment.priority)}>
                                        {priorityEmoji(assignment.priority)} {t(priorityKey(assignment.priority) || '')}
                                    </XtramBadge>
                                </TableCell>
                                <TableCell>
                                    <XtramBadge tone={statusTone(assignment.status)}>
                                        {t(statusKey(assignment.status) || '')}
                                    </XtramBadge>
                                </TableCell>
                                <TableCell>
                                    {assignment.dueDate ? dayjs(assignment.dueDate).format('DD/MM/YYYY') : '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                        {rows.length > PREVIEW_ROWS && (
                            <TableRow>
                                <TableCell colSpan={6} className={styles.actionsCell}>
                                    <span className={styles.fieldHint}>+{rows.length - PREVIEW_ROWS} {t('More')}</span>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </XtramTable>
        </div>
    );

    return (
        <XtramModal
            open={open}
            onClose={onClose}
            direction={direction}
            expanded
            busy={loading}
            title={t('AssignmentReportManager')}
            footer={(
                <>
                    <XtramButton variant="secondary" onClick={onClose} disabled={loading}>
                        {t('Cancel')}
                    </XtramButton>
                    <XtramButton variant="ghost" onClick={handleDownloadPdf} disabled={loading}>
                        <PdfIcon fontSize="small" />
                        {t('DownloadPdf')}
                    </XtramButton>
                    <XtramButton variant="primary" onClick={handleDownloadExcel} disabled={loading} busy={loading}>
                        <ExcelIcon fontSize="small" />
                        {t('DownloadExcel')}
                    </XtramButton>
                </>
            )}
        >
            <div className={styles.section}>
                <div className={styles.stats}>
                    <XtramStat label={t('TotalAssignments')} value={filteredAssignments.length} />
                    <XtramStat label={t('ToDo')} value={todoAssignments.length} tone="warning" />
                    <XtramStat label={t('InProgress')} value={inProgressAssignments.length} />
                    <XtramStat label={t('Done')} value={doneAssignments.length} tone="success" />
                </div>
            </div>

            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>{t('ReportConfiguration')}</h3>
                <div className={styles.formFields}>
                    <XtramField label={t('Client')}>
                        <XtramSelect
                            direction={direction}
                            options={clientOptions}
                            value={selectedOption(clientOptions, reportFilters.caseId)}
                            onChange={pickFilter('caseId')}
                            placeholder={t('SelectClient')}
                        />
                    </XtramField>
                    <XtramField label={t('Assignee')}>
                        <XtramSelect
                            direction={direction}
                            options={assigneeOptions}
                            value={selectedOption(assigneeOptions, reportFilters.assigneeId)}
                            onChange={pickFilter('assigneeId')}
                            placeholder={t('SelectAssignee')}
                        />
                    </XtramField>
                    <XtramField label={t('Status')}>
                        <XtramSelect
                            direction={direction}
                            options={statusOptions}
                            value={selectedOption(statusOptions, reportFilters.status)}
                            onChange={pickFilter('status')}
                            placeholder={t('SelectStatus')}
                        />
                    </XtramField>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16 }}>
                {renderAssignmentTable(todoAssignments, t('ToDo'), 'warning')}
                {renderAssignmentTable(inProgressAssignments, t('InProgress'), 'info')}
                {renderAssignmentTable(doneAssignments, t('Done'), 'success')}
            </div>
        </XtramModal>
    );
};

export default AssignmentReportManager;
